import React, { useContext, useEffect, useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { decode } from 'shopify-gid'
import { ProductContext, LayoutContext, useUI } from '../contexts'
import useReplaceLineItem from '././../hooks/useReplaceLineItem'
import Price from './Price'
import { SiteLink } from './shared/SiteLink'
import useProductTitleTranslation from '../hooks/useProductTitleTranslation'
import { useProductRemovedTracking } from '../hooks/tracking'
import { useShopify } from '../contexts/shopify'
import { useGlobal } from '../hooks/useGlobal'

const LineItem = ({ lineItem, key }) => {
  const {
    node: {
      variant: {
        presentmentPrices: { edges },
      },
    },
  } = lineItem

  const [discountPrice, setDiscountPrice] = useState('')
  const [originalPrice, setOriginalPrice] = useState(
    edges[0].node.compareAtPrice,
  )

  //--------------Context-----------

  const { node_locale, currency, storeSubdomain } = useContext(LayoutContext)

  const {
    cart: { outOfStock, remove, showSpringSaleTieredDiscountMessage },
    product: { size, showTieredDiscountMessage },
    layout: { giftWrappingProduct },
    blackFriday: { blackFridayThemeColor, showAnnouncementBarBlackFriday2022 },
  } = useGlobal(['cart', 'product', 'layout', 'blackFriday'], node_locale)

  const { setCartOpen } = useUI()

  //--------------Selectors-----------

  const { checkouts: allCheckouts } = useShopify()
  const checkouts = allCheckouts[storeSubdomain]

  const isGiftWrapping =
    lineItem.node.variant.product.handle === giftWrappingProduct.handle

  const products = useContext(ProductContext)
  const productsArray = Object.values(products)
  const productSlug = productsArray.find(
    ({ shopifyProduct, shopifyProduct2 }) =>
      lineItem.node.variant.product.handle === shopifyProduct.handle ||
      (shopifyProduct2 &&
        lineItem.node.variant.product.handle === shopifyProduct2.handle),
  )?.slug

  const [replaceLineItem, _] = useReplaceLineItem()
  const [getProductTitleTranslation, { title, loading, error }] =
    useProductTitleTranslation()

  const discountAmount = lineItem.node.discountAllocations.reduce(
    (acc, { allocatedAmount: { amount } }) =>
      parseFloat(acc) + parseFloat(amount),
    0,
  )

  useEffect(() => {
    getProductTitleTranslation(lineItem.node.id, node_locale)
  }, [checkouts.lineItems.edges.length])

  useEffect(() => {
    const {
      node: {
        variant: {
          presentmentPrices: { edges },
        },
      },
    } = lineItem
    const originalPrice = edges[0].node.compareAtPrice

    if (originalPrice) {
      setOriginalPrice(
        parseFloat(
          edges.find(
            ({
              node: {
                compareAtPrice: { currencyCode },
              },
            }) => currencyCode === currency.code,
          ).node.compareAtPrice.amount,
        ),
      )
      setDiscountPrice(
        parseFloat(
          edges.find(
            ({
              node: {
                price: { currencyCode },
              },
            }) => currencyCode === currency.code,
          ).node.price.amount,
        ),
      )
    }
  }, [lineItem])

  return (
    <StyledLineItem key={key}>
      <ProductImageWrapper>
        <ProductImageLink
          to={productSlug}
          productSlug={productSlug && lineItem.node.variant.availableForSale}
          onClick={() => setCartOpen(false)}
        >
          <ProductImage src={lineItem.node.variant.image.transformedSrc} />
        </ProductImageLink>
      </ProductImageWrapper>
      <ProductInfo>
        <ProductTitleLink
          to={productSlug}
          productSlug={productSlug && lineItem.node.variant.availableForSale}
          onClick={() => setCartOpen(false)}
        >
          {error ? (
            <ProductTitle>{lineItem.node.title}</ProductTitle>
          ) : (
            <ProductTitle loading={loading}>{title}</ProductTitle>
          )}
        </ProductTitleLink>

        {/* Prices */}
        <div>
          <ProductPrice
            strikethrough={
              (discountAmount > 0 &&
                (showTieredDiscountMessage ||
                  showSpringSaleTieredDiscountMessage)) ||
              originalPrice
            }
            colorStrikethrough={
              showAnnouncementBarBlackFriday2022 ? blackFridayThemeColor : null
            }
          >
            <Price
              lineItem={lineItem}
              compareAtPrice={originalPrice}
              amount={lineItem.node.quantity}
            />
          </ProductPrice>
          {!isGiftWrapping && originalPrice && discountPrice && (
            <ProductDiscountPrice color={blackFridayThemeColor}>
              <Price lineItem={lineItem} amount={lineItem.node.quantity} />
            </ProductDiscountPrice>
          )}
        </div>
        {discountAmount > 0 &&
          (showTieredDiscountMessage ||
            showSpringSaleTieredDiscountMessage) && (
            <ProductDiscountPrice
              color={
                showAnnouncementBarBlackFriday2022
                  ? blackFridayThemeColor
                  : null
              }
            >
              <Price
                lineItem={lineItem}
                amount={lineItem.node.quantity}
                discount={discountAmount}
              />
            </ProductDiscountPrice>
          )}
        {/* .end prices */}

        <ProductDetails>
          <span>{size}:</span> {lineItem.node.variant.selectedOptions[0].value}
        </ProductDetails>
        <Controls>
          {lineItem.node.variant.availableForSale ? (
            <Amount>
              <StyledQuantityInput>
                <ControlButton
                  type='button'
                  aria-label='quantity add one less'
                  onClick={() => {
                    replaceLineItem({
                      variantId: lineItem.node.variant.id,
                      quantity: lineItem.node.quantity - 1,
                    })
                  }}
                >
                  -
                </ControlButton>
                <Quantity>{lineItem.node.quantity}</Quantity>
                <ControlButton
                  type='button'
                  aria-label='quantity add one more'
                  disabled={isGiftWrapping && lineItem.node.quantity >= 10}
                  onClick={() => {
                    replaceLineItem({
                      variantId: lineItem.node.variant.id,
                      quantity: lineItem.node.quantity + 1,
                    })
                  }}
                >
                  +
                </ControlButton>
              </StyledQuantityInput>
            </Amount>
          ) : (
            <OutOfStock>
              {outOfStock} ({lineItem.node.variant.selectedOptions[0].value})
            </OutOfStock>
          )}
          <Remove>
            <button
              type='button'
              onClick={() => {
                replaceLineItem({
                  variantId: lineItem.node.variant.id,
                  quantity: 0,
                })
                useProductRemovedTracking({
                  currency: currency?.code,
                  locale: node_locale,
                  checkoutId: decode(checkouts.id).id,
                  // productId: decode(lineItem.node.variant.product.id).id,
                  // variantId: decode(lineItem.node.variant.id).id,
                  productId: decode(lineItem.node.variant.id).id,
                  sku: lineItem.node.variant.sku,
                  name: title || lineItem.node.title,
                  variant: lineItem.node.variant.title,
                  price: lineItem.node.variant.presentmentPrices.edges.find(
                    ({
                      node: {
                        price: { currencyCode },
                      },
                    }) => currencyCode === currency?.code,
                  ).node.price.amount,
                  quantity: lineItem.node.quantity,
                  brand: 'Stox',
                  category: 'Socks',
                  value:
                    lineItem.node.variant.presentmentPrices.edges.find(
                      ({
                        node: {
                          price: { currencyCode },
                        },
                      }) => currencyCode === currency?.code,
                    ).node.price.amount * lineItem.node.quantity,
                  imageUrl: lineItem.node.variant.image.transformedSrc,
                })
              }}
            >
              {remove}
            </button>
          </Remove>
        </Controls>
      </ProductInfo>
    </StyledLineItem>
  )
}

const fadeIn = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`

const strikethroughAnim = keyframes`
  from {
    transform: scaleX(0) translateY(-50%);
  }
  to {
    transform: scaleX(1) translateY(-50%);
  }
`

const StyledLineItem = styled.div`
  display: flex;
  margin: 0 ${(props) => props.theme.space.marginMobile}vw;
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  border-bottom: 1px solid ${(props) => props.theme.colors.mediumGrey};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin: 0;
    padding-top: 1.2vw;
    padding-bottom: 1.2vw;
  }
`

const ProductImageWrapper = styled.div`
  background: #f6f6f6;
  flex: 3;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 8vw;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const ProductImageLink = styled(SiteLink)`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: ${(props) => (props.productSlug ? 'auto' : 'none')};
`

const ProductImage = styled.img`
  max-width: 100%;
  object-fit: cover;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 80%;
    width: 80%;
  }
`

const ProductInfo = styled.div`
  flex: 6;
  padding-left: ${(props) => props.theme.space.marginMobile}vw;
  display: flex;
  flex-wrap: wrap;
  position: relative;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    flex: 7;
    padding-left: ${(props) => props.theme.space.marginDesktop * 2}vw;
    justify-content: space-between;
  }
`

const ProductTitleLink = styled(SiteLink)`
  pointer-events: ${(props) => (props.productSlug ? 'auto' : 'none')};
  width: 78%;
`

const ProductTitle = styled.h2`
  font-size: ${(props) => props.theme.fontSize.m26};
  font-family: ${(props) => props.theme.fontFamily.bold};
  width: 100%;

  animation: ${(props) =>
    props.loading === 'completed'
      ? css`
          ${fadeIn} 0.1s both
        `
      : 'none'};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
  }
`

const ProductPrice = styled.div`
  font-size: ${(props) => props.theme.fontSize.m26};
  text-align: right;
  align-self: flex-start;
  position: relative;
  white-space: nowrap;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
  }

  &::after {
    content: '';
    display: ${(props) => (props.strikethrough ? 'block' : 'none')};
    background: ${(props) =>
      props.colorStrikethrough || props.theme.colors.blue};
    height: 1px;
    width: 110%;
    position: absolute;
    top: 50%;
    right: -7%;
    transform-origin: left center;
    transform: scaleX(0) translateY(-50%);
    animation: ${strikethroughAnim} 0.8s 0.2s
      ${(props) => props.theme.easeOut.expo} forwards;
  }
`

const ProductDiscountPrice = styled.div`
  font-size: ${(props) => props.theme.fontSize.m26};
  text-align: right;
  position: absolute;
  top: 4vw;
  right: 0;
  color: ${(props) => props.color || props.theme.colors.blue};
  opacity: 0;
  animation: ${fadeIn} 0.8s 0.8s ${(props) => props.theme.easeOut.expo} forwards;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    top: 1vw;
    font-size: ${(props) => props.theme.fontSize.d14};
  }
`

const ProductDetails = styled.div`
  font-size: ${(props) => props.theme.fontSize.m20};
  text-transform: uppercase;
  width: 100%;
  letter-spacing: 0.05em;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
  }
`

const Controls = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Amount = styled.div`
  width: 50%;
  height: ${(props) => props.theme.space.marginMobile * 3.5}vw;
  align-self: flex-end;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 2.47vw;
  }
`

const OutOfStock = styled.div`
  display: inline-block;
  color: #8b2527;
  font-size: ${(props) => props.theme.fontSize.m26};
  text-transform: uppercase;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
  }
`

const Remove = styled.div`
  button {
    text-transform: uppercase;
    font-size: ${(props) => props.theme.fontSize.m23};
    cursor: pointer;
    letter-spacing: 0.05em;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d12};
      align-self: flex-end;
    }
  }
`

const StyledQuantityInput = styled.div`
  border: 1px solid ${(props) => props.theme.colors.mediumGrey};
  display: flex;
  flex: 1;
  height: calc(100% - 2px);
  align-items: center;
`

const ControlButton = styled.button`
  font-family: ${(props) => props.theme.fontFamily.normal};
  font-size: ${(props) => props.theme.fontSize.m26};
  height: 100%;
  flex: 1;
  color: ${(props) => props.theme.colors.grey};
  cursor: pointer;

  &:disabled {
    opacity: 0.2;
    cursor: no-drop;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
  }
`

const Quantity = styled.div`
  font-family: ${(props) => props.theme.fontFamily.bold};
  font-size: ${(props) => props.theme.fontSize.m26};
  color: ${(props) => props.theme.colors.black};
  text-align: center;
  width: ${(props) => props.theme.space.marginMobile * 2}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
    width: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

export default LineItem
